import { format, isToday, isYesterday, isTomorrow, addDays, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';

/**
 * Форматирует дату в короткий формат (Сегодня, Завтра, дд.мм.гггг)
 * 
 * @param {string|Date} date - Дата для форматирования
 * @returns {string} - Отформатированная дата
 */
export const formatShortDate = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  if (isToday(dateObj)) {
    return 'Today';
  } else if (isTomorrow(dateObj)) {
    return 'Tomorrow';
  } else if (isYesterday(dateObj)) {
    return 'Yesterday';
  }
  
  return format(dateObj, 'dd.MM.yyyy');
};

/**
 * Форматирует дату в полный формат (день недели, дд месяц гггг)
 * 
 * @param {string|Date} date - Дата для форматирования
 * @returns {string} - Отформатированная дата
 */
export const formatFullDate = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  return format(dateObj, 'EEEE, dd MMMM yyyy', { locale: enUS });
};

/**
 * Форматирует дату в средний формат (день недели, дд месяц)
 * 
 * @param {string|Date} date - Дата для форматирования
 * @returns {string} - Отформатированная дата без года
 */
export const formatMediumDate = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  return format(dateObj, 'EEEE, dd MMMM', { locale: enUS });
};

/**
 * Форматирует дату и время (дд.мм.гггг ЧЧ:ММ)
 * 
 * @param {string|Date} date - Дата для форматирования
 * @returns {string} - Отформатированная дата и время
 */
export const formatDateTime = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  return format(dateObj, 'dd.MM.yyyy HH:mm');
};

/**
 * Форматирует время из даты (ЧЧ:ММ)
 * 
 * @param {string|Date} date - Дата для форматирования
 * @returns {string} - Отформатированное время
 */
export const formatTime = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  return format(dateObj, 'HH:mm');
};

/**
 * Группирует массив объектов по дате
 * 
 * @param {Array} items - Массив объектов с полем даты
 * @param {string} dateField - Имя поля с датой в объекте
 * @returns {Object} - Объект, сгруппированный по дате
 */
export const groupByDate = (items, dateField = 'matchDate') => {
  if (!items || !items.length) return {};
  
  return items.reduce((groups, item) => {
    const date = item[dateField];
    if (!date) return groups;
    
    const dateKey = format(new Date(date), 'yyyy-MM-dd');
    
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    
    groups[dateKey].push(item);
    return groups;
  }, {});
};

/**
 * Получает диапазон дат для следующих N дней
 * 
 * @param {number} days - Количество дней в диапазоне
 * @returns {Array} - Массив дат
 */
export const getDateRange = (days = 7) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const dateRange = [];
  
  for (let i = 0; i < days; i++) {
    const date = addDays(today, i);
    dateRange.push(date);
  }
  
  return dateRange;
};

/**
 * Создает строку для URL из даты (yyyy-mm-dd)
 * 
 * @param {Date|string} date - Дата для форматирования
 * @returns {string} - Дата в формате yyyy-mm-dd
 */
export const formatDateForUrl = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  return format(dateObj, 'yyyy-MM-dd');
}; 